/**
 * LandingFooter — MADE-260
 *
 * Landing-page-specific footer.
 * Includes Staff Login as a low-key text link (NOT in primary nav).
 * The ShopFooter component (retail-web/Next.js) is a separate component
 * and cannot be imported here; this serves the same role for the Vite landing page.
 */

import { Box, Container, Typography } from '@mui/material';

export function LandingFooter() {
  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#1a1a2e',
        py: { xs: 5, md: 6 },
        px: 2,
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'center', md: 'flex-start' },
            justifyContent: 'space-between',
            gap: 4,
          }}
        >
          {/* Brand / copyright */}
          <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            {/* Logo pill — white container makes the RGB (non-transparent) PNG legible on dark bg */}
            <Box
              sx={{
                display: 'inline-flex',
                backgroundColor: 'rgba(255,255,255,0.92)',
                borderRadius: 1.5,
                px: 1.5,
                py: 0.75,
                mb: 1.5,
              }}
            >
              <Box
                component="img"
                src="/madebb/logo.png"
                alt="Made.bb"
                sx={{ height: 84, width: 'auto', display: 'block' }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.8rem' }}
            >
              © Made.bb {currentYear}
            </Typography>
          </Box>

          {/* Footer links */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: { xs: 2.5, md: 3.5 },
              justifyContent: { xs: 'center', md: 'flex-end' },
              alignItems: 'center',
            }}
          >
            {[
              { label: 'Shop', href: '/shop' },
              { label: 'Custom Print', href: '/shop/custom-print' },
              { label: 'About', href: '/shop/about' },
              { label: 'Contact', href: '/shop/contact' },
              { label: 'Terms', href: '/shop/terms' },
              { label: 'Privacy', href: '/shop/privacy' },
            ].map(({ label, href }) => (
              <Box
                key={href}
                component="a"
                href={href}
                sx={{
                  color: 'rgba(255,255,255,0.55)',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 400,
                  transition: 'color 0.2s',
                  '&:hover': { color: 'rgba(255,255,255,0.9)' },
                }}
              >
                {label}
              </Box>
            ))}

            {/* Divider */}
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                width: '1px',
                height: 14,
                backgroundColor: 'rgba(255,255,255,0.14)',
                display: { xs: 'none', md: 'block' },
                mx: 0.5,
              }}
            />

            {/* Staff Login — deliberately low-key, not in primary nav */}
            <Box
              component="a"
              href="/madebb/login"
              data-testid="footer-link-staff-login"
              sx={{
                color: 'rgba(255,255,255,0.28)',
                textDecoration: 'none',
                fontSize: '0.78rem',
                transition: 'color 0.2s',
                '&:hover': { color: 'rgba(255,255,255,0.55)' },
              }}
            >
              Staff Login
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
